import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ThemeContext } from './themePreference'
import { CHART_PALETTES, PALETTES } from '@/theme/tokens'
import {
  applyTheme,
  readThemePreference,
  resolveTheme,
  SYSTEM_THEME_QUERY,
  THEME_STORAGE_KEY,
  themePreference,
  type ThemePreference,
} from '@/theme/preference'

/** Root channel; changing its value never keys or remounts application content. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, updatePreference] = useState(readThemePreference)
  const [systemDark, setSystemDark] = useState(() => {
    try {
      return window.matchMedia(SYSTEM_THEME_QUERY).matches
    } catch {
      return true
    }
  })
  const [storageAvailable, setStorageAvailable] = useState(true)
  const mode = resolveTheme(preference, systemDark)

  useLayoutEffect(() => {
    applyTheme(mode)
  }, [mode])

  useLayoutEffect(() => {
    let media: MediaQueryList | undefined
    const syncSystem = () => setSystemDark(media?.matches ?? true)
    try {
      media = window.matchMedia(SYSTEM_THEME_QUERY)
      syncSystem()
      media.addEventListener('change', syncSystem)
    } catch {
      /* Device appearance unavailable; System resolves to Dark. */
    }
    const syncStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY && event.key !== null) return
      try {
        if (event.storageArea && event.storageArea !== window.localStorage)
          return
      } catch {
        return
      }
      updatePreference(themePreference(event.newValue))
      setStorageAvailable(true)
    }
    window.addEventListener('storage', syncStorage)
    try {
      window.localStorage.getItem(THEME_STORAGE_KEY)
    } catch {
      setStorageAvailable(false)
    }
    return () => {
      media?.removeEventListener('change', syncSystem)
      window.removeEventListener('storage', syncStorage)
    }
  }, [])

  const setPreference = useCallback((next: ThemePreference) => {
    updatePreference(next)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next)
      setStorageAvailable(true)
    } catch {
      // The selection still works for this tab until reload.
      setStorageAvailable(false)
    }
  }, [])
  const value = useMemo(
    () => ({
      preference,
      mode,
      palette: PALETTES[mode],
      chartPalette: CHART_PALETTES[mode],
      storageAvailable,
      setPreference,
    }),
    [preference, mode, storageAvailable, setPreference]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
